/**
 * Novel Analysis Page
 *
 * 小說分析結果頁面，包含 6 個 Tab：摘要、角色、關係、場景、視角、風格
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAnalysisStatus } from '../hooks/useAnalysisStatus';
import {
  novelAnalysisApi,
  type AnalysisResults,
} from '../services/api/novel-analysis';
import { API_BASE } from '../services/api/client';
import { SummaryTab } from './analysis/SummaryTab';
import { CharactersTab } from './analysis/CharactersTab';
import { RelationshipsTab } from './analysis/RelationshipsTab';
import { ScenesTab } from './analysis/ScenesTab';
import { PerspectivesTab } from './analysis/PerspectivesTab';
import { StyleTab } from './analysis/StyleTab';

type TabType = 'summary' | 'characters' | 'relationships' | 'scenes' | 'perspectives' | 'style';

const ANALYSIS_STEPS = [
  { key: 'summary', label: '摘要' },
  { key: 'characters', label: '角色' },
  { key: 'sex_scenes', label: '場景' },
  { key: 'perspectives', label: '視角' },
] as const;

const TABS: { key: TabType; label: string }[] = [
  { key: 'summary', label: '摘要' },
  { key: 'characters', label: '角色' },
  { key: 'relationships', label: '關係' },
  { key: 'scenes', label: '場景' },
  { key: 'perspectives', label: '視角' },
  { key: 'style', label: '風格' },
];

export default function NovelAnalysisPage() {
  const { id: novelId } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('summary');
  const [results, setResults] = useState<AnalysisResults | null>(null);
  const [resultsLoading, setResultsLoading] = useState(false);

  // Refresh results on each status poll (for in_progress partial results)
  const loadResultsRef = useRef<() => void>();
  const { status, loading: statusLoading, refresh: refreshStatus, startPolling } = useAnalysisStatus(
    novelId,
    () => loadResultsRef.current?.()
  );
  const [novelTitle, setNovelTitle] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load novel title
  useEffect(() => {
    if (!novelId) return;
    fetch(`${API_BASE}/novels/${novelId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d: { data?: { title?: string } }) => setNovelTitle(d.data?.title || ''))
      .catch(() => {});
  }, [novelId]);

  // Load results when in_progress, completed, or failed
  const loadResults = useCallback(async () => {
    if (!novelId) return;
    const validStatuses = ['in_progress', 'completed', 'failed'];
    if (!status || !validStatuses.includes(status.status)) return;
    setResultsLoading(true);
    try {
      const data = await novelAnalysisApi.getResults(novelId);
      setResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load results');
    } finally {
      setResultsLoading(false);
    }
  }, [novelId, status?.status]);

  // Keep ref in sync for onUpdate callback
  loadResultsRef.current = loadResults;

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const handleStart = async () => {
    if (!novelId) return;
    setActionLoading(true);
    setError(null);
    try {
      await novelAnalysisApi.startAnalysis(novelId);
      await refreshStatus();
      startPolling();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!novelId) return;
    setActionLoading(true);
    setError(null);
    try {
      await novelAnalysisApi.retryAnalysis(novelId);
      await refreshStatus();
      startPolling();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClear = async () => {
    if (!novelId || !confirm('確定要清除所有分析資料？')) return;
    setActionLoading(true);
    try {
      await novelAnalysisApi.clearAnalysis(novelId);
      setResults(null);
      await refreshStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear');
    } finally {
      setActionLoading(false);
    }
  };

  const parseJson = (str: string | null): unknown => {
    if (!str) return null;
    try { return JSON.parse(str); } catch { return null; }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/novels/${novelId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 返回小說
          </Link>
          <h1 className="text-xl font-bold">{novelTitle}</h1>
          <StatusBadge status={status?.status || 'not_started'} />
        </div>
        <div className="flex gap-2">
          {status?.status && status.status !== 'in_progress' && status.status !== 'not_started' && (
            <button onClick={handleClear} disabled={actionLoading}
              className="px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50">
              清除資料
            </button>
          )}
          {status?.status === 'failed' && (
            <button onClick={handleRetry} disabled={actionLoading}
              className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50">
              {actionLoading ? '重試中...' : '重試'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Not Started */}
      {(!status || status.status === 'not_started') && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500 mb-4">尚未進行分析。點擊下方按鈕開始 AI 分析。</p>
          <button onClick={handleStart} disabled={actionLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {actionLoading ? '啟動中...' : '開始分析'}
          </button>
        </div>
      )}

      {/* In Progress */}
      {status?.status === 'in_progress' && (
        <ProgressPanel status={status} />
      )}

      {/* Failed */}
      {status?.status === 'failed' && status.error_log.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 rounded">
          <h3 className="font-medium text-red-800 mb-2">分析錯誤</h3>
          {status.error_log.map((err, i) => (
            <div key={i} className="text-sm text-red-600">
              第 {err.chapter_index} 章 ({err.step}): {err.error}
            </div>
          ))}
        </div>
      )}

      {/* Results Tabs */}
      {results && (results.chapters.length > 0 || results.characters.length > 0) && (
        <>
          <div className="flex border-b">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {resultsLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : results ? (
            <div className="bg-white rounded-lg shadow">
              {activeTab === 'summary' && <SummaryTab chapters={results.chapters} parseJson={parseJson} />}
              {activeTab === 'characters' && <CharactersTab characters={results.characters} parseJson={parseJson} />}
              {activeTab === 'relationships' && <RelationshipsTab relationships={results.relationships} parseJson={parseJson} />}
              {activeTab === 'scenes' && <ScenesTab scenes={results.sex_scenes} parseJson={parseJson} />}
              {activeTab === 'perspectives' && <PerspectivesTab perspectives={results.perspectives} parseJson={parseJson} />}
              {activeTab === 'style' && <StyleTab style={results.writing_style} parseJson={parseJson} />}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ============================================================
// Sub Components
// ============================================================

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    not_started: 'bg-gray-100 text-gray-600',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    paused: 'bg-yellow-100 text-yellow-700',
  };
  const labels: Record<string, string> = {
    not_started: '未開始',
    in_progress: '分析中',
    completed: '已完成',
    failed: '失敗',
    paused: '已暫停',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.not_started}`}>
      {labels[status] || status}
    </span>
  );
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '剛才';
  if (mins < 60) return `${mins} 分鐘前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小時 ${mins % 60} 分鐘前`;
  return `${Math.floor(hours / 24)} 天前`;
}

function formatAvgTime(startedAt: string, processedChapters: number): string {
  const elapsed = Date.now() - new Date(startedAt).getTime();
  const avgMs = elapsed / processedChapters;
  const avgSec = Math.round(avgMs / 1000);
  if (avgSec < 60) return `${avgSec}s`;
  return `${Math.floor(avgSec / 60)}m ${avgSec % 60}s`;
}

function ProgressPanel({ status }: { status: { processed_chapters: number; total_chapters: number; current_step: string | null; current_chapter_index: number | null; started_at: string | null } }) {
  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      {/* Overall progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
          <span className="font-medium text-gray-900">
            正在分析第 {Math.min(status.processed_chapters + 1, status.total_chapters)} / {status.total_chapters} 章
          </span>
        </div>
        <span className="text-sm font-medium text-blue-600">
          {Math.round((status.processed_chapters / Math.max(status.total_chapters, 1)) * 100)}%
        </span>
      </div>

      {/* Main progress bar */}
      <div className="w-full bg-gray-100 rounded-full h-2.5">
        <div
          className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
          style={{ width: `${(status.processed_chapters / Math.max(status.total_chapters, 1)) * 100}%` }}
        />
      </div>

      {/* Current chapter step-by-step */}
      {status.current_step && (
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-xs text-gray-500 mb-3">
            第 {status.current_chapter_index ?? 1} 章分析步驟
          </p>
          <div className="flex items-center gap-1">
            {ANALYSIS_STEPS.map((step, i) => {
              const currentIdx = ANALYSIS_STEPS.findIndex((s) => s.key === status.current_step);
              const isDone = i < currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                      isDone ? 'bg-green-100 text-green-700' :
                      isCurrent ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' :
                      'bg-gray-100 text-gray-400'
                    }`}>
                      {isDone ? '✓' : i + 1}
                    </div>
                    <span className={`text-[10px] mt-1 text-center leading-tight ${
                      isCurrent ? 'text-blue-700 font-medium' : isDone ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < ANALYSIS_STEPS.length - 1 && (
                    <div className={`h-0.5 w-full mt-[-12px] ${isDone ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Chapter progress timeline */}
      <div>
        <p className="text-xs text-gray-500 mb-2">章節進度</p>
        <div className="flex gap-0.5 flex-wrap">
          {Array.from({ length: status.total_chapters }, (_, i) => {
            const isDone = i < status.processed_chapters;
            const isCurrent = i === ((status.current_chapter_index ?? status.processed_chapters + 1) - 1);
            return (
              <div
                key={i}
                title={`第 ${i + 1} 章${isDone ? ' (已完成)' : isCurrent ? ' (分析中)' : ''}`}
                className={`h-3 rounded-sm transition-all ${
                  status.total_chapters > 50 ? 'w-1.5' : status.total_chapters > 20 ? 'w-2.5' : 'w-4'
                } ${
                  isDone ? 'bg-green-400' :
                  isCurrent ? 'bg-blue-500 animate-pulse' :
                  'bg-gray-200'
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Time info */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>
          已完成 {status.processed_chapters} 章
          {status.processed_chapters > 0 && status.started_at && (
            <> · 平均 {formatAvgTime(status.started_at, status.processed_chapters)}/章</>
          )}
        </span>
        {status.started_at && <span>開始於 {formatRelativeTime(status.started_at)}</span>}
      </div>
    </div>
  );
}
